import { useNavigate } from '@tanstack/react-location';
import React, { useMemo, useState } from 'react';
import useAuthentication from '../../../api/use-authentication';
import { useFlexAssessmentContext } from '../../../context/context';
import buttons from '../../../css/buttons.module.scss';
import styles from '../../../css/modal.module.scss';
import { FlexAssessmentContextAction } from '../../../types/flex-assessment-context-action';
import { NotificationType } from '../../../types/notification-type';
import { MIXPANEL_EVENTS } from '../../../utils/mixpanel-consts';
import { track } from '../../../utils/mixpanel-helper';
import { STRINGS } from '../../../utils/strings';
import { validatePassword } from '../../../utils/validation';
import Input from '../../form-fields/input';
import PasswordAndStrength from '../../password-and-strength/password-and-strength';
import Spinner, { SPINNER_COLORS } from '../../spinner';
import { HASH_URLS } from '../auth-modal';

export const ChooseNewPasswordForm = React.memo((): JSX.Element => {
  const navigate = useNavigate();
  const { resetPassword } = useAuthentication();
  const { state, dispatch } = useFlexAssessmentContext();
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [passwordClone, setPasswordClone] = useState<string>('');

  const isValid = useMemo(() => {
    return validatePassword(newPassword) && newPassword === passwordClone;
  }, [newPassword, passwordClone]);

  const onPasswordChange = async (
    event: React.FormEvent<HTMLButtonElement>
  ): Promise<void> => {
    event.preventDefault();
    setIsSubmittingForm(true);
    track(MIXPANEL_EVENTS.BUTTON_CLICK, { button: 'password-change' });
    const req = await resetPassword({
      email: state.email.value as string,
      password: newPassword,
      code: verificationCode,
    });
    setIsSubmittingForm(false);
    if (req === 'Password Changed') {
      dispatch({
        type: FlexAssessmentContextAction.showNotification,
        payload: {
          message: STRINGS.SUCCESS.PASSWORD_RESET_SUCCESS,
          type: NotificationType.Success,
        },
      });
      setTimeout(() => {
        dispatch({ type: FlexAssessmentContextAction.dismissNotification });
        navigate({ to: `#${HASH_URLS.LOGIN}` });
      }, 4000);
    }
  };

  return (
    <form className={styles.modalContent}>
      <h3 className={styles.modalTitle}>Reset your password</h3>
      <p className={styles.p}>
        We’ve sent an email to {state.email.value}. Please enter the
        verification code you received and create a new password.
      </p>
      <Input
        type="text"
        value={verificationCode}
        name="verification-code"
        placeholder="Verification Code"
        validator={(value: string): boolean => value.length === 6}
        errorString={STRINGS.ERRORS.INCORRECT_VERIFICATION_CODE}
        callback={(
          name: string,
          value: string,
          isValid: boolean | undefined
        ): void => {
          setVerificationCode(value);
        }}
      />
      <PasswordAndStrength
        name="PasswordAndStrength"
        value={newPassword}
        autoComplete="new-password"
        callback={(
          name: string,
          value: string,
          isValid: boolean | undefined
        ): void => {
          setNewPassword(value);
        }}
      />
      <Input
        type="password"
        value={passwordClone}
        autoComplete="new-password"
        name="password-clone"
        placeholder="Re-type password"
        validator={(value: string): boolean => {
          return value === newPassword;
        }}
        errorString={STRINGS.ERRORS.PASSWORDS_DONT_MATCH}
        callback={(
          name: string,
          value: string,
          isValid: boolean | undefined
        ): void => {
          setPasswordClone(value);
        }}
      />
      <button
        onClick={onPasswordChange}
        className={buttons.submitBtn}
        disabled={!isValid || isSubmittingForm}
      >
        {isSubmittingForm && <Spinner color={SPINNER_COLORS.LIGHT} />}
        Submit
      </button>
    </form>
  );
});
